import React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { DURATION, EASE_OUT, STAGGER, TRAVEL } from './variants';

/* ============================================================
   Stagger — a container whose children animate in sequence.
   Pair <Stagger> with <StaggerItem> children (any depth).
   ============================================================ */

/** Standard DOM props (incl. className, onSubmit, aria-*) minus the
    handful framer-motion redefines with different signatures. */
type DOMProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

type ContainerTag = 'div' | 'ul' | 'section' | 'form' | 'header' | 'p';

type StaggerProps = {
  children: React.ReactNode;
  /** Gap between children (seconds). */
  stagger?: number;
  /** Delay before the first child (seconds). */
  delay?: number;
  /** Fraction of the container visible before triggering. */
  amount?: number;
  as?: ContainerTag;
} & DOMProps;

const CONTAINER_TAGS = {
  div: motion.div,
  ul: motion.ul,
  section: motion.section,
  form: motion.form,
  header: motion.header,
  p: motion.p,
};

export const Stagger: React.FC<StaggerProps> = ({
  children,
  stagger = STAGGER,
  delay = 0,
  amount = 0.2,
  as = 'div',
  ...rest
}) => {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    const Plain = as as React.ElementType;
    return <Plain {...rest}>{children}</Plain>;
  }

  const variants: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger, delayChildren: delay },
    },
  };

  const Tag = CONTAINER_TAGS[as] as React.ElementType;

  return (
    <Tag
      {...rest}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
    >
      {children}
    </Tag>
  );
};

/* ------------------------------------------------------------ */

type ItemTag = 'div' | 'li' | 'article' | 'span';
/** `fade` animates opacity only — use it on elements that already
    own a CSS `transform` (tilts, hover effects) so motion does not
    overwrite it. */
type ItemDirection = 'up' | 'left' | 'right' | 'scale' | 'fade';

type ItemProps = {
  children: React.ReactNode;
  direction?: ItemDirection;
  /** Adds a subtle lift on hover. */
  hover?: boolean;
  as?: ItemTag;
} & DOMProps;

const ITEM_TAGS = {
  div: motion.div,
  li: motion.li,
  article: motion.article,
  span: motion.span,
};

const variantsFor = (direction: ItemDirection): Variants => {
  const transition = { duration: DURATION.item, ease: EASE_OUT };
  switch (direction) {
    case 'fade':
      // Opacity only — never touch transform.
      return {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition },
      };
    case 'left':
      return {
        hidden: { opacity: 0, x: -TRAVEL },
        visible: { opacity: 1, x: 0, transition },
      };
    case 'right':
      return {
        hidden: { opacity: 0, x: TRAVEL },
        visible: { opacity: 1, x: 0, transition },
      };
    case 'scale':
      return {
        hidden: { opacity: 0, scale: 0.92 },
        visible: { opacity: 1, scale: 1, transition },
      };
    default:
      return {
        hidden: { opacity: 0, y: TRAVEL },
        visible: { opacity: 1, y: 0, transition },
      };
  }
};

/**
 * A single staggered child. Inherits the hidden/visible state from a
 * parent <Stagger> — no `initial`/`whileInView` of its own.
 */
export const StaggerItem: React.FC<ItemProps> = ({
  children,
  direction = 'up',
  hover = false,
  as = 'div',
  ...rest
}) => {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    const Plain = as as React.ElementType;
    return <Plain {...rest}>{children}</Plain>;
  }

  const variants = variantsFor(direction);

  const Tag = ITEM_TAGS[as] as React.ElementType;

  return (
    <Tag
      {...rest}
      variants={variants}
      whileHover={hover ? { y: -6 } : undefined}
      transition={{ duration: 0.3, ease: EASE_OUT }}
    >
      {children}
    </Tag>
  );
};
